<script setup lang="ts">
import { computed, ref } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { apiPost } from '@/lib/http'

interface TeacherRef {
  user_id: number
  profile_image?: string
  last_name?: string
  first_name?: string
}
interface StudentItem {
  id: number
  teachers: TeacherRef[]
  [key: string]: unknown
}
interface JoinRequest {
  teacher: TeacherRef
  jstudent: { id?: number; first_name?: string; last_name?: string }
}

const props = defineProps<{
  students?: StudentItem[]
}>()

const emit = defineEmits<{
  (e: 'create-student', payload: { first_name?: string; last_name?: string }): void
}>()

const { notify } = useNotification()

const open = ref(false)
const request = ref<JoinRequest | null>(null)
const selectedStudent = ref<number | null>(null)
const isStudentPreselected = ref(false)

const availableStudents = computed(() => {
  if (!request.value) return []
  const teacherId = request.value.teacher.user_id

  let students = (props.students ?? []).filter(student => {
    return student.teachers.every(e => teacherId !== e.user_id)
  })

  if (isStudentPreselected.value) {
    const selectedStudentIndex = students.findIndex(s => s.id === selectedStudent.value)

    const [selected] = students.splice(selectedStudentIndex, 1)
    students = [selected, ...students]
  }

  return students
})

function $open(requestValue: JoinRequest, selectedStudentId?: number) {
  open.value = true
  request.value = requestValue
  selectedStudent.value = selectedStudentId ?? null
  isStudentPreselected.value = selectedStudentId !== undefined
}

function createStudent() {
  open.value = false
  emit('create-student', {
    first_name: request.value?.jstudent?.first_name,
    last_name: request.value?.jstudent?.last_name
  })
}

async function rejectJoin() {
  try {
    const { failed, message } = await apiPost('/parent/request_reject_join_teacher', {
      jstudent_id: request.value?.jstudent?.id
    })

    if (failed) throw message
    window.location.reload()
  } catch (err) {
    let title = 'Произошла ошибка при попытке отклонить запрос.'
    if (typeof err === 'string') title = err
    notify({ type: 'error', title })
  }
}

async function confirmJoin() {
  try {
    const { failed, message } = await apiPost('/parent/request_confirm_join_teacher', {
      jstudent_id: request.value?.jstudent?.id,
      student_id: selectedStudent.value
    })

    if (failed) throw message
    window.location.reload()
  } catch (err) {
    let title = 'Произошла ошибка при попытке принять запрос.'
    if (typeof err === 'string') title = err
    notify({ type: 'error', title })
  }
}

defineExpose({
  $open
})
</script>

<template>
  <v-rich-modal v-model="open" title="Запрос от учителя" persistent>
    <div v-if="request" class="join-request">
      <div class="join-request__header mb-2">
        <img :src="request.teacher.profile_image" />
        <div class="ma-1" />

        <p>
          Учитель
          <strong>{{ request.teacher.last_name }} {{ request.teacher.first_name }}</strong>
          добавил в свою группу вашего ребёнка с именем
          <strong>{{ request.jstudent.last_name }} {{ request.jstudent.first_name }}</strong>
        </p>
      </div>

      <p class="text-body-bold my-1 join-request__center-text">Кто из ваших детей соответствует данному запросу?</p>

      <ul class="join-request__children-container">
        <li
          v-for="student in availableStudents"
          :key="student.id"
          class="join-request__child py-2 px-3"
          :class="{ 'join-request__child_active': selectedStudent === student.id }"
          @click="() => (selectedStudent = student.id)"
        >
          <v-icon v-if="selectedStudent === student.id" class="blue-text"> mdi-radiobox-marked </v-icon>
          <v-icon v-else class="gray-text"> mdi-radiobox-blank </v-icon>

          <span class="flex-spacer ml-2">{{ student.name }}</span>
        </li>
      </ul>

      <v-link mode="primary block" class="my-1" @click="createStudent">
        <v-icon class="mr-1">mdi-plus</v-icon>
        Добавить нового ребёнка
      </v-link>
    </div>

    <template #actions>
      <div class="join-request__footer">
        <p class="mb-2 text-caption">
          Обратите внимание: после подтверждения учитель сможет видеть активность вашего ребёнка на сайте.
        </p>

        <div class="join-request__actions">
          <v-button mode="error outlined block" @click="rejectJoin"> Отклонить </v-button>
          <div class="ma-1" />
          <v-button mode="primary filled block" :disabled="!selectedStudent" @click="confirmJoin">
            Подтвердить
          </v-button>
        </div>
      </div>
    </template>
  </v-rich-modal>
</template>

<style lang="scss">
.join-request {
  display: flex;
  flex-direction: column;

  @include desktop {
    width: 450px;
  }

  @include mobile {
    height: 100%;
  }

  &__center-text {
    text-align: center;
  }

  &__child {
    display: flex;
    border: 1px solid $gray;
    border-radius: 8px;

    margin: 16px 0;
    font-weight: bold;
    cursor: pointer;

    &_active {
      border-color: $blue;
    }
  }

  &__footer {
    @include desktop {
      width: 515px;
    }
  }

  &__actions {
    display: flex;

    @include mobile {
      flex-direction: column-reverse;
    }
  }

  &__header {
    display: flex;
    align-items: center;

    & > img {
      width: 100px;
      height: 100px;
      border-radius: 999px;
    }
  }
}
</style>
